import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SocialButton from "@/components/SocialButton";
import ScrollToTop from "@/components/ScrollToTop";
import AboutBand from "@/components/AboutBand";

import Counter from "@/components/Counter";
import FileTree from "@/components/FileTree";
import QuoteButton from "@/components/QuoteButton";
import ProjectsDeck from "@/components/ProjectsDeck";
import Operations, { type Operation } from "@/components/Operations";
import {
  ConsultingFlow,
  EmbeddedSeats,
  SocialFrames,
} from "@/components/ServiceVisuals";
import ServicesScenes, { type Service } from "@/components/ServicesScenes";

import { useTheme } from "@/lib/theme";
import { useHeaderVisibility } from "@/lib/header";
import { useSeo } from "@/lib/seo";

import operationsData from "@/data/operations.json";
import { useProjects } from "@/lib/projects";

import map from "@/assets/map.webp";
import metric from "@/assets/metric.webp";

import BlurText from "@/components/BlurText";
import DotGrid from "@/components/DotGrid";
import SpotlightCard from "@/components/SpotlightCard";

const whatsapp = (message: string) =>
  `https://wa.me/5511967611652?text=${encodeURIComponent(message)}`;

const SPOTLIGHT_LIFE_MS = 30000;

export default function Home() {
  const { t } = useTranslation();
  const isDark = useTheme() === "dark";

  const dotBaseColor = isDark ? "#555555" : "#8fa4c9";
  const dotActiveColor = isDark ? "#999999" : "#002366";

  const footerRef = useRef<HTMLDivElement>(null);
  const headerVisible = useHeaderVisibility(footerRef);

  useSeo({
    title: t("meta.home.title"),
    description: t("meta.home.description"),
    path: "/",
  });

  const [spotlightVisible, setSpotlightVisible] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setSpotlightVisible(false),
      SPOTLIGHT_LIFE_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  const projectsRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const [dotsStatic, setDotsStatic] = useState(false);

  useEffect(() => {
    const targets = [projectsRef.current, servicesRef.current].filter(
      (el): el is HTMLDivElement => el !== null,
    );
    if (targets.length === 0) return;

    const inView = new Map<Element, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
        setDotsStatic([...inView.values()].some(Boolean));
      },
      { threshold: 0 },
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  const projects = useProjects();
  const operations = operationsData as Operation[];

  const socials = (
    <>
      <SocialButton href="https://www.instagram.com/suportededomingo/">
        {t("ui.instagram")}
      </SocialButton>

      <SocialButton href="https://discord.gg/qB42JUJquU">
        {t("ui.discord")}
      </SocialButton>
    </>
  );

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  const quote = (key: string) =>
    t("contact.quoteMessage", {
      service: t(`services.cards.${key}.title`),
    });

  const services: Service[] = [
    {
      key: "uiUx",
      title: t("services.cards.uiUx.title"),
      description: t("services.cards.uiUx.desc"),
      href: whatsapp(quote("uiUx")),
      visual: (
        <img
          src={map}
          alt=""
          width={800}
          height={457}
          className="w-full max-w-[560px] h-auto opacity-90 dark:opacity-60"
        />
      ),
    },
    {
      key: "socialMedia",
      title: t("services.cards.socialMedia.title"),
      description: t("services.cards.socialMedia.desc"),
      href: whatsapp(quote("socialMedia")),
      visual: <SocialFrames />,
    },
    {
      key: "branding",
      title: t("services.cards.branding.title"),
      description: t("services.cards.branding.desc"),
      href: whatsapp(quote("branding")),
      visual: (
        <div className="w-full max-w-[520px] flex rounded-2xl overflow-hidden h-40">
          <div className="flex-1/5 bg-[#002366]"></div>
          <div className="flex-1/5 bg-[#001D56]"></div>
          <div className="flex-1/5 bg-[#011946]"></div>
          <div className="flex-1/5 bg-[#001235]"></div>
          <div className="flex-1/5 bg-[#000D25]"></div>
        </div>
      ),
    },
    {
      key: "marketing",
      title: t("services.cards.marketing.title"),
      description: t("services.cards.marketing.desc"),
      href: whatsapp(quote("marketing")),
      visual: (
        <div className="relative w-full max-w-[560px]">
          <div className="flex flex-col gap-4 text-brand dark:text-[#BFC0C0]">
            <div className="flex gap-2 items-center">
              <ArrowUpCircle size={20} />
              <Counter number={58.12} duration={0.5} />
            </div>
            <div className="flex gap-2 items-center">
              <ArrowUpCircle size={20} />
              <Counter number={34.27} duration={0.8} />
            </div>
            <div className="flex gap-2 items-center">
              <ArrowUpCircle size={20} />
              <Counter number={64.26} duration={1} />
            </div>
          </div>

          <img
            src={metric}
            alt=""
            width={801}
            height={349}
            className="mt-6 w-full h-auto"
          />
        </div>
      ),
    },
    {
      key: "development",
      title: t("services.cards.development.title"),
      description: t("services.cards.development.desc"),
      href: whatsapp(quote("development")),
      visual: <FileTree />,
    },
    {
      key: "embedded",
      title: t("services.cards.embedded.title"),
      description: t("services.cards.embedded.desc"),
      href: whatsapp(t("contact.embeddedMessage")),
      visual: (
        <EmbeddedSeats
          clientLabel={t("services.cards.embedded.clientTeam")}
        />
      ),
    },
    {
      key: "consulting",
      title: t("services.cards.consulting.title"),
      description: t("services.cards.consulting.desc"),
      href: whatsapp(quote("consulting")),
      visual: (
        <ConsultingFlow
          steps={
            t("services.cards.consulting.steps", {
              returnObjects: true,
            }) as string[]
          }
        />
      ),
    },
  ];

  return (

    <main className="flex flex-col background overflow-x-clip">
      <div
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          headerVisible
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-10 pointer-events-none"
        }`}
      >
        <Header />
      </div>

      <section className="page-shell text-ink flex flex-col justify-between px-4 md:px-8 xl:px-16 pt-24 md:pt-32 lg:pt-34 min-h-svh z-1 gap-4">
        <div className="flex w-full flex-1 flex-col justify-between gap-6">
          <BlurText
            priority
            delay={200}
            animateBy="words"
            text={t("hero.lineOne")}
            className="font-medium text-7xl md:text-[calc(6.5vw+8px)]"
          />
          <BlurText
            priority
            delay={350}
            animateBy="words"
            text={t("hero.lineTwo")}
            className="self-start font-medium text-7xl md:text-[calc(6.5vw+8px)] md:self-end"
          />
          <BlurText
            priority
            delay={350}
            animateBy="words"
            text={t("hero.lineThree")}
            className="font-medium text-7xl md:text-[calc(6.5vw+8px)]"
          />
          <BlurText
            priority
            delay={200}
            animateBy="words"
            text={t("hero.lineFour")}
            className="self-start font-medium text-7xl md:text-[calc(6.5vw+8px)] md:self-end"
          />
        </div>
        <div className="w-full flex flex-col pb-8 items-center h-fit gap-8 text-ink md:flex lg:flex-row lg:items-end justify-between ">
          <motion.div
            className="hidden md:flex w-full md:w-3/5 lg:w-1/2 2xl:w-2/5"
            initial={false}
            animate={
              spotlightVisible
                ? { opacity: 1, y: 0, filter: "blur(0px)" }
                : { opacity: 0, y: -16, filter: "blur(10px)" }
            }
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            style={{ pointerEvents: spotlightVisible ? "auto" : "none" }}
            aria-hidden={!spotlightVisible}
          >
            <SpotlightCard className="flex w-full p-5 2xl:p-6 border-2 border-line dark:border-[#212121]/20 rounded-lg backdrop-blur-md dark:backdrop-blur-sm bg-gradient-overlay shadow-[0_8px_24px_rgba(11,27,58,0.06)] dark:shadow-none">
              <p className="text-left font-light text-lg 2xl:text-xl">
                {t("hero.spotlight")}
              </p>
            </SpotlightCard>
          </motion.div>

          <div>
            <ArrowDownCircle
              className="text-brand dark:text-[#BFC0C0] relative h-8 w-auto cursor-pointer z-1"
              onClick={() => {
                window.scrollBy({
                  top: window.innerHeight * 0.96,
                  behavior: "smooth",
                });
              }}
              aria-label={t("hero.scrollDownAria")}
            />
          </div>
        </div>
      </section>

      <div ref={projectsRef} className="z-1">
        <ProjectsDeck projects={projects} />
      </div>

      <AboutBand
        eyebrow={t("about.heading")}
        paragraphs={[t("about.paragraph"), t("about.paragraphTwo")]}
        centers={t("about.centers")}
        cta={socials}
      />

      <div className="text-ink z-1">
        <Operations operations={operations} />
      </div>

      <span id="servicos"></span>

      <div ref={servicesRef} className="text-ink z-1">
        <ServicesScenes
          intro={{
            eyebrow: t("common.services"),
            title: t("services.introLineOne"),
            support: t("services.introLineTwo"),
          }}
          services={services}
          cta={
            <QuoteButton
              href={whatsapp(t("contact.quoteGeneric"))}
            >
              {t("services.cta")}
            </QuoteButton>
          }
        />
      </div>

      <div ref={footerRef} className="z-1 ">
        <Footer />
      </div>

      <div className="pointer-events-none w-100 h-100 m-2 hidden md:block fixed -bottom-4 -left-4 opacity-30 dark:opacity-10">
        <DotGrid
          dotSize={5}
          gap={25}
          baseColor={dotBaseColor}
          activeColor={dotActiveColor}
          proximity={dotsStatic ? 0 : 100}
          shockRadius={dotsStatic ? 0 : 250}
          shockStrength={dotsStatic ? 0 : 5}
          resistance={750}
          returnDuration={1.5}
        />
      </div>

      <div className="pointer-events-none w-100 h-100 m-2 hidden md:block fixed -top-4 -right-4 opacity-30 dark:opacity-10">
        <DotGrid
          dotSize={5}
          gap={25}
          baseColor={dotBaseColor}
          activeColor={dotActiveColor}
          proximity={dotsStatic ? 0 : 100}
          shockRadius={dotsStatic ? 0 : 250}
          shockStrength={dotsStatic ? 0 : 5}
          resistance={750}
          returnDuration={1.5}
        />
      </div>

      <ScrollToTop />
    </main>
  );
}
